import { supabase } from '../lib/supabase';

/**
 * Payment History Service - Handles all payment history related operations
 * Converts between snake_case (DB) and camelCase (React)
 */

export const paymentHistoryService = {
  /**
   * Get payment transactions with order details for current user
   */
  async getTransactionHistory(userId, filters = {}) {
    try {
      let query = supabase?.from('payment_transactions')?.select(`
          *,
          orders (
            order_number,
            project_name,
            project_description,
            subtotal,
            tax_amount,
            total_amount,
            currency,
            payment_status,
            created_at,
            updated_at
          )
        `)?.eq('orders.user_id', userId)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.startDate) {
        query = query?.gte('created_at', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('created_at', filters?.endDate);
      }

      if (filters?.minAmount) {
        query = query?.gte('amount', filters?.minAmount);
      }

      if (filters?.maxAmount) {
        query = query?.lte('amount', filters?.maxAmount);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert to camelCase
      return data?.map(transaction => ({
        id: transaction?.id,
        orderId: transaction?.order_id,
        paymentIntentId: transaction?.payment_intent_id,
        stripeChargeId: transaction?.stripe_charge_id,
        amount: parseFloat(transaction?.amount),
        currency: transaction?.currency,
        status: transaction?.status,
        transactionType: transaction?.transaction_type,
        gateway: transaction?.gateway,
        metadata: transaction?.metadata,
        createdAt: transaction?.created_at,
        order: transaction?.orders ? {
          orderNumber: transaction?.orders?.order_number,
          projectName: transaction?.orders?.project_name,
          projectDescription: transaction?.orders?.project_description,
          subtotal: parseFloat(transaction?.orders?.subtotal),
          taxAmount: parseFloat(transaction?.orders?.tax_amount),
          totalAmount: parseFloat(transaction?.orders?.total_amount),
          currency: transaction?.orders?.currency,
          paymentStatus: transaction?.orders?.payment_status,
          createdAt: transaction?.orders?.created_at,
          updatedAt: transaction?.orders?.updated_at
        } : null
      }));
    } catch (error) {
      console.error('Error fetching transaction history:', error);
      throw error;
    }
  },

  /**
   * Get payment methods with expiration status
   */
  async getPaymentMethods(userId) {
    try {
      const { data, error } = await supabase?.from('payment_methods_with_expiration')?.select('*')?.eq('user_id', userId)?.order('is_default', { ascending: false })?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert to camelCase
      return data?.map(method => ({
        id: method?.id,
        userId: method?.user_id,
        stripePaymentMethodId: method?.stripe_payment_method_id,
        cardBrand: method?.card_brand,
        cardLast4: method?.card_last4,
        cardExpMonth: method?.card_exp_month,
        cardExpYear: method?.card_exp_year,
        isDefault: method?.is_default,
        isExpired: method?.is_expired,
        expiresSoon: method?.expires_soon,
        billingName: method?.billing_name,
        billingAddressLine1: method?.billing_address_line1,
        billingAddressLine2: method?.billing_address_line2,
        billingCity: method?.billing_city,
        billingState: method?.billing_state,
        billingPostalCode: method?.billing_postal_code,
        billingCountry: method?.billing_country,
        createdAt: method?.created_at,
        updatedAt: method?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw error;
    }
  },

  /**
   * Get outstanding invoices (pending orders)
   */
  async getOutstandingInvoices(userId) {
    try {
      const { data, error } = await supabase?.from('orders')?.select('*')?.eq('user_id', userId)?.in('payment_status', ['pending', 'failed'])?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert to camelCase
      return data?.map(order => ({
        id: order?.id,
        orderNumber: order?.order_number,
        projectName: order?.project_name,
        projectDescription: order?.project_description,
        subtotal: parseFloat(order?.subtotal),
        taxAmount: parseFloat(order?.tax_amount),
        totalAmount: parseFloat(order?.total_amount),
        currency: order?.currency,
        paymentStatus: order?.payment_status,
        paymentIntentId: order?.payment_intent_id,
        status: order?.status,
        createdAt: order?.created_at,
        updatedAt: order?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching outstanding invoices:', error);
      throw error;
    }
  },

  /**
   * Search transactions by query string
   */
  async searchTransactions(userId, searchQuery) {
    try {
      const { data, error } = await supabase?.from('payment_transactions')?.select(`
          *,
          orders (
            order_number,
            project_name,
            subtotal,
            tax_amount,
            total_amount,
            currency,
            payment_status
          )
        `)?.eq('orders.user_id', userId)?.or(`payment_intent_id.ilike.%${searchQuery}%,stripe_charge_id.ilike.%${searchQuery}%`)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert to camelCase
      return data?.map(transaction => ({
        id: transaction?.id,
        orderId: transaction?.order_id,
        paymentIntentId: transaction?.payment_intent_id,
        stripeChargeId: transaction?.stripe_charge_id,
        amount: parseFloat(transaction?.amount),
        currency: transaction?.currency,
        status: transaction?.status,
        createdAt: transaction?.created_at,
        order: transaction?.orders ? {
          orderNumber: transaction?.orders?.order_number,
          projectName: transaction?.orders?.project_name,
          subtotal: parseFloat(transaction?.orders?.subtotal),
          taxAmount: parseFloat(transaction?.orders?.tax_amount),
          totalAmount: parseFloat(transaction?.orders?.total_amount),
          currency: transaction?.orders?.currency,
          paymentStatus: transaction?.orders?.payment_status
        } : null
      }));
    } catch (error) {
      console.error('Error searching transactions:', error);
      throw error;
    }
  }
};